﻿import { useMemo, useState } from 'react'
import { sileo } from 'sileo'
import {
  DETECTION_SOURCES,
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  RESPONSE_SLA_HOURS,
} from '../../services/incidentService'

/**
 * Convierte un ISO/timestamp a el formato que exige `<input type="datetime-local">`
 * (hora local, sin zona ni segundos).
 * @param {string|Date|null} value
 * @returns {string}
 */
const toLocalInput = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

/**
 * Convierte el valor de un `<input type="datetime-local">` a ISO UTC.
 * @param {string} value
 * @returns {string|null}
 */
const toIso = (value) => (value ? new Date(value).toISOString() : null)

/**
 * Calcula el vencimiento de respuesta sugerido a partir de la detección
 * y la severidad, según {@link RESPONSE_SLA_HOURS}.
 * @param {string} detectedAtLocal - Valor del input datetime-local.
 * @param {string} severity
 * @returns {string} Valor listo para un input datetime-local.
 */
const suggestDueAt = (detectedAtLocal, severity) => {
  const hours = RESPONSE_SLA_HOURS[severity]
  if (!detectedAtLocal || !hours) return ''
  const base = new Date(detectedAtLocal)
  if (Number.isNaN(base.getTime())) return ''
  return toLocalInput(new Date(base.getTime() + hours * 3600000))
}

/**
 * Estado inicial del formulario a partir de un incidente existente (edición)
 * o de valores por defecto (registro nuevo).
 * @param {Object|null} incident
 * @param {string} author - Nombre del usuario actual.
 * @returns {Object}
 */
const buildInitialState = (incident, author) => {
  if (incident) {
    return {
      DetectedAt: toLocalInput(incident.DetectedAt),
      ReportedBy: incident.ReportedBy || '',
      DetectionSource: incident.DetectionSource || '',
      Description: incident.Description || '',
      Severity: incident.Severity || 'Medio',
      ResponseDueAt: toLocalInput(incident.ResponseDueAt),
      Containment: incident.Containment || '',
      Eradication: incident.Eradication || '',
      PersonalDataAffected: !!incident.PersonalDataAffected,
      ProdhabRequired: !!incident.ProdhabRequired,
      ProdhabNotifiedAt: toLocalInput(incident.ProdhabNotifiedAt),
      RootCause: incident.RootCause || '',
      CorrectiveActions: incident.CorrectiveActions || '',
      Status: incident.Status || 'Abierto',
    }
  }

  const now = toLocalInput(new Date())
  return {
    DetectedAt: now,
    ReportedBy: author,
    DetectionSource: DETECTION_SOURCES[0],
    Description: '',
    Severity: 'Medio',
    ResponseDueAt: suggestDueAt(now, 'Medio'),
    Containment: '',
    Eradication: '',
    PersonalDataAffected: false,
    ProdhabRequired: false,
    ProdhabNotifiedAt: '',
    RootCause: '',
    CorrectiveActions: '',
    Status: 'Abierto',
  }
}

/**
 * Formulario de registro y edición de incidentes de seguridad (SGSI).
 *
 * El estado inicial se calcula una sola vez al montar: el consumidor debe
 * pasar un `key` distinto por incidente para que React lo remonte al cambiar
 * de registro (ver `Incidents.jsx`).
 *
 * @component
 * @param {Object} props
 * @param {Object|null} [props.incident=null] - Incidente a editar; `null` para registrar uno nuevo.
 * @param {Array<Object>} props.assets - Catálogo de activos del SGSI.
 * @param {string} props.author - Nombre del usuario actual.
 * @param {boolean} [props.saving=false] - Deshabilita el submit mientras guarda.
 * @param {'full'|'report'} [props.mode='full'] - `report` es el formulario que ve
 *   cualquier colaborador: sólo captura lo que presenció (qué, cuándo, qué activos).
 *   El triage —estado, plazo, contención, causa raíz y PRODHAB— es del equipo de
 *   seguridad y sólo aparece en `full`.
 * @param {string} [props.submitLabel] - Texto del botón principal.
 * @param {(payload: Object, assetIds: Array<number>) => Promise<boolean>} props.onSubmit
 * @param {(() => void)|null} [props.onCancel=null] - Si es `null` no se muestra "Cancelar".
 * @returns {JSX.Element}
 */
const IncidentForm = ({
  incident = null,
  assets = [],
  author,
  saving = false,
  mode = 'full',
  submitLabel,
  onSubmit,
  onCancel = null,
}) => {
  const isTriage = mode === 'full'
  const [form, setForm] = useState(() => buildInitialState(incident, author))
  const [assetIds, setAssetIds] = useState(
    () => incident?.assets?.map((asset) => asset.Id) ?? []
  )
  const [assetQuery, setAssetQuery] = useState('')
  // Deja de auto-sugerir el vencimiento en cuanto el usuario lo edita a mano.
  const [dueTouched, setDueTouched] = useState(!!incident?.ResponseDueAt)

  const isEdit = !!incident

  const filteredAssets = useMemo(() => {
    const term = assetQuery.trim().toLowerCase()
    if (!term) return assets
    return assets.filter(
      (asset) =>
        asset.Name?.toLowerCase().includes(term) ||
        asset.Code?.toLowerCase().includes(term) ||
        asset.Type?.toLowerCase().includes(term)
    )
  }, [assets, assetQuery])

  /**
   * Actualiza un campo del formulario, recalculando el vencimiento sugerido
   * cuando cambian la detección o la severidad.
   * @param {string} field
   * @param {*} value
   */
  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value }

      if (!dueTouched && (field === 'DetectedAt' || field === 'Severity')) {
        next.ResponseDueAt = suggestDueAt(next.DetectedAt, next.Severity)
      }
      // Sin datos personales afectados no aplica la notificación a PRODHAB.
      if (field === 'PersonalDataAffected' && !value) {
        next.ProdhabRequired = false
        next.ProdhabNotifiedAt = ''
      }

      return next
    })
  }

  const toggleAsset = (id) => {
    setAssetIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!form.Description.trim()) {
      sileo.error({ title: 'La descripción es obligatoria' })
      return
    }
    if (!form.DetectedAt) {
      sileo.error({ title: 'Indica la fecha de detección' })
      return
    }

    // En modo reporte el incidente entra siempre como 'Abierto' y sin datos
    // de triage: esos campos los completa después el equipo de seguridad.
    const closing = isTriage && form.Status === 'Cerrado'

    const payload = {
      DetectedAt: toIso(form.DetectedAt),
      ReportedBy: form.ReportedBy.trim() || null,
      DetectionSource: form.DetectionSource.trim() || null,
      Description: form.Description.trim(),
      Severity: form.Severity,
      // El plazo de respuesta se deriva de la severidad aunque el reportante
      // no vea el campo, para que el SLA quede fijado desde el registro.
      ResponseDueAt: toIso(
        isTriage
          ? form.ResponseDueAt
          : suggestDueAt(form.DetectedAt, form.Severity)
      ),
      PersonalDataAffected: form.PersonalDataAffected,
      Status: isTriage ? form.Status : 'Abierto',
    }

    if (isTriage) {
      Object.assign(payload, {
        Containment: form.Containment.trim() || null,
        Eradication: form.Eradication.trim() || null,
        ProdhabRequired: form.PersonalDataAffected
          ? form.ProdhabRequired
          : false,
        ProdhabNotifiedAt: form.PersonalDataAffected
          ? toIso(form.ProdhabNotifiedAt)
          : null,
        RootCause: form.RootCause.trim() || null,
        CorrectiveActions: form.CorrectiveActions.trim() || null,
        // El cierre queda sellado con quién y cuándo; reabrir lo limpia.
        ClosedAt: closing
          ? incident?.ClosedAt || new Date().toISOString()
          : null,
        ClosedBy: closing ? incident?.ClosedBy || author : null,
      })
    }

    const ok = await onSubmit(payload, assetIds)
    if (ok) onCancel?.()
  }

  return (
    <form onSubmit={handleSubmit} className="incident-form">
      {isEdit && (
        <div className="alert alert-secondary py-2 px-3 small mb-3">
          Editando <strong>{incident.Code}</strong> · registrado por{' '}
          {incident.CreatedBy || 'desconocido'}
        </div>
      )}

      <p className="form-section-title">Detección</p>
      <div className="row g-3 mb-3">
        <div className="col-md-6" data-tour="inc-detected">
          <label className="form-label small fw-semibold">
            Fecha de detección <span className="text-danger">*</span>
          </label>
          <input
            type="datetime-local"
            className="form-control"
            value={form.DetectedAt}
            onChange={(e) => setField('DetectedAt', e.target.value)}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label small fw-semibold">
            Severidad <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={form.Severity}
            onChange={(e) => setField('Severity', e.target.value)}
          >
            {INCIDENT_SEVERITIES.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label small fw-semibold">Reportado por</label>
          <input
            type="text"
            className="form-control"
            value={form.ReportedBy}
            onChange={(e) => setField('ReportedBy', e.target.value)}
            placeholder="Nombre de quien reporta"
          />
        </div>
        <div className="col-md-6" data-tour="inc-source">
          <label className="form-label small fw-semibold">
            Origen de detección
          </label>
          <input
            type="text"
            className="form-control"
            list="incident-detection-sources"
            value={form.DetectionSource}
            onChange={(e) => setField('DetectionSource', e.target.value)}
            placeholder="¿Cómo se detectó?"
          />
          <datalist id="incident-detection-sources">
            {DETECTION_SOURCES.map((source) => (
              <option key={source} value={source} />
            ))}
          </datalist>
        </div>
        <div className="col-12" data-tour="inc-description">
          <label className="form-label small fw-semibold">
            Descripción <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={form.Description}
            onChange={(e) => setField('Description', e.target.value)}
            placeholder="Qué ocurrió, cuándo, a quién afectó…"
            required
          />
        </div>
        {isTriage && (
          <>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">
                Respuesta antes de
              </label>
              <input
                type="datetime-local"
                className="form-control"
                value={form.ResponseDueAt}
                onChange={(e) => {
                  setDueTouched(true)
                  setField('ResponseDueAt', e.target.value)
                }}
              />
              <div className="form-text small">
                Sugerido: {RESPONSE_SLA_HOURS[form.Severity]} h desde la
                detección.
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Estado</label>
              <select
                className="form-select"
                value={form.Status}
                onChange={(e) => setField('Status', e.target.value)}
              >
                {INCIDENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      <p className="form-section-title">Activos afectados</p>
      <div className="mb-3" data-tour="inc-assets">
        <input
          type="text"
          className="form-control form-control-sm mb-2"
          placeholder="Filtrar activos…"
          value={assetQuery}
          onChange={(e) => setAssetQuery(e.target.value)}
        />
        <div className="asset-picker">
          {filteredAssets.length === 0 ? (
            <p className="text-body-secondary small mb-0 p-2">
              No hay activos que coincidan.
            </p>
          ) : (
            filteredAssets.map((asset) => (
              <label key={asset.Id} className="asset-option">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={assetIds.includes(asset.Id)}
                  onChange={() => toggleAsset(asset.Id)}
                />
                <span className="flex-grow-1 min-w-0">
                  <span className="d-block small fw-medium text-truncate">
                    {asset.Name}
                  </span>
                  <span
                    className="d-block text-body-secondary"
                    style={{ fontSize: 11 }}
                  >
                    {asset.Code} · {asset.Type}
                  </span>
                </span>
                {asset.Classification && (
                  <span className="badge bg-body-secondary text-body border">
                    {asset.Classification}
                  </span>
                )}
              </label>
            ))
          )}
        </div>
        <div className="form-text small">
          {assetIds.length} activo(s) seleccionado(s).
        </div>
      </div>

      <p className="form-section-title">Datos personales</p>
      <div className="mb-3" data-tour="inc-personal">
        <div className="form-check">
          <input
            id="incident-personal-data"
            type="checkbox"
            className="form-check-input"
            checked={form.PersonalDataAffected}
            onChange={(e) => setField('PersonalDataAffected', e.target.checked)}
          />
          <label
            className="form-check-label small"
            htmlFor="incident-personal-data"
          >
            El incidente afectó datos personales
          </label>
        </div>

        {form.PersonalDataAffected && isTriage && (
          <div className="row g-3 mt-1">
            <div className="col-md-6 d-flex align-items-center">
              <div className="form-check">
                <input
                  id="incident-prodhab"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.ProdhabRequired}
                  onChange={(e) =>
                    setField('ProdhabRequired', e.target.checked)
                  }
                />
                <label
                  className="form-check-label small"
                  htmlFor="incident-prodhab"
                >
                  Requiere notificación a PRODHAB
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">
                Notificado a PRODHAB el
              </label>
              <input
                type="datetime-local"
                className="form-control"
                value={form.ProdhabNotifiedAt}
                onChange={(e) => setField('ProdhabNotifiedAt', e.target.value)}
                disabled={!form.ProdhabRequired}
              />
            </div>
          </div>
        )}
      </div>

      {isTriage && (
        <>
          <p className="form-section-title">Respuesta y cierre</p>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Contención</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.Containment}
                onChange={(e) => setField('Containment', e.target.value)}
                placeholder="Medidas inmediatas para frenar el incidente"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">
                Erradicación
              </label>
              <textarea
                className="form-control"
                rows={2}
                value={form.Eradication}
                onChange={(e) => setField('Eradication', e.target.value)}
                placeholder="Cómo se eliminó la causa del entorno"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Causa raíz</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.RootCause}
                onChange={(e) => setField('RootCause', e.target.value)}
                placeholder="Por qué ocurrió"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">
                Acciones correctivas
              </label>
              <textarea
                className="form-control"
                rows={2}
                value={form.CorrectiveActions}
                onChange={(e) => setField('CorrectiveActions', e.target.value)}
                placeholder="Qué se hará para que no se repita"
              />
            </div>
          </div>
        </>
      )}

      <div className="d-flex justify-content-end gap-2">
        {onCancel && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          data-tour="inc-submit"
          className="btn btn-primary"
          disabled={saving}
        >
          {saving && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
            />
          )}
          {submitLabel || (isEdit ? 'Guardar cambios' : 'Registrar incidente')}
        </button>
      </div>

      <style>{`
        .form-section-title {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--gm-text-muted);
          margin-bottom: 0.5rem;
          padding-bottom: 0.35rem;
          border-bottom: 1px solid var(--gm-border);
        }
        .asset-picker {
          max-height: 210px;
          overflow-y: auto;
          border: 1px solid var(--gm-border);
          border-radius: 10px;
          background: var(--gm-surface);
        }
        .asset-option {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          cursor: pointer;
          border-bottom: 1px solid var(--gm-border);
        }
        .asset-option:last-child { border-bottom: none; }
        .asset-option:hover { background: var(--gm-surface-2); }
        .min-w-0 { min-width: 0; }
      `}</style>
    </form>
  )
}

export default IncidentForm
